import AddressFormField, { FieldType } from 'checkout/components/AddressFormField'
import CountryDropdownField from 'checkout/components/CountryDropdownField'
import NewShippingAddressForm from 'checkout/components/ShippingAddressPanel/NewShippingAddressForm'
import StateDropdownField from 'checkout/components/StateDropdownField'
import { useAddressFormContext } from 'checkout/containers/AddressFormContextProvider'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { Form, Formik } from 'formik'
import Cookies from 'js-cookie'
import { t } from 'localization'
import AuthFormField, { FieldType as AuthFieldType } from 'login/components/AuthFormField'
import { useEffect } from 'react'
import { emailValidation, getAddressValidationFields } from 'shared/addressValidations'
import { Button } from 'shared/components/Button'
import FormikCheckbox from 'shared/components/Checkbox/FormikCheckbox'
import Spinner from 'shared/components/Spinner'
import { CheckoutQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { STORAGE } from 'shared/enums/SitePreferences'
import { colors, fonts } from 'shared/lib'
import { setUser } from 'store/authSlice'
import { useAppDispatch } from 'store/hooks'
import styled from 'styled-components'
import * as Yup from 'yup'

const TP = 'checkout.components.ShippingAddressPanel.GuestShippingAddressForm'

const ContinueButton = styled(Button)`
  margin-bottom: 15px;
`

const Pointer = styled.div`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
  margin: 20px 0;
`

const EmailSignUp = styled(FormikCheckbox)`
  margin: 10px 0;
`

const GuestShippingAddressForm = () => {
  const isGuestCheckout = useFeatureFlag(FeatureFlag.GUEST_CHECKOUT)
  const isEmailSignupPrecheckEnabled = useFeatureFlag(
    FeatureFlag.WEB_FC_ENABLE_PRECHECK_EMAIL_SIGNUP,
  )

  const dispatch = useAppDispatch()

  const {
    availableCountries,
    isStateRequired,
    isZipRequired,
    countryISOCode,
    countryUpdateHandler,
  } = useAddressFormContext()

  const {
    createGuestOrderWithShippingAddress,
    panels: { shippingAddress },
  } = useCheckoutContext()

  const guestShippingAddressFormSchema = Yup.object({
    email: emailValidation(),
    isNewsletterActive: Yup.boolean(),
    ...getAddressValidationFields(isZipRequired, isStateRequired),
  })

  const checkIfShippable = () => {
    const defaultCountry = availableCountries.find(
      (country) => country.isoCode === Cookies.get(STORAGE.COUNTRY) && country.shipsTo,
    )
    return defaultCountry?.isoCode || ''
  }

  // Preselect newsletter checkbox if allowed by cookied country
  const precheckEmailSignup =
    isEmailSignupPrecheckEnabled &&
    !!availableCountries.find((country) => country.isoCode === Cookies.get(STORAGE.COUNTRY))
      ?.allowPrecheckEmailSignup

  const initialGuestShipValues = {
    address1: '',
    address2: undefined,
    city: '',
    countryCode: checkIfShippable(),
    email: '',
    isNewsletterActive: precheckEmailSignup,
    name: '',
    phone: undefined,
    postalCode: '',
    state: '',
  }

  const handleSubmitForm = async (values) => {
    await createGuestOrderWithShippingAddress(values)
    const guestUser = {
      email: values.email,
      isGuest: true,
      name: 'Guest User',
    }
    dispatch(setUser(guestUser))
  }

  // Run counntry update handler on mount if country is defaulted by cookie
  useEffect(() => {
    if (initialGuestShipValues.countryCode !== '') {
      countryUpdateHandler(initialGuestShipValues.countryCode)
    }
  }, [])

  const handleCountryChange = countryUpdateHandler

  if (!isGuestCheckout) {
    return <NewShippingAddressForm />
  }

  return (
    <Spinner
      showSpinner={shippingAddress.loading}
      text={t(`${TP}.spinnerText`, 'Saving address...')}
      subText={t(`${TP}.spinnerSubText`, 'Please do not close your browser!')}
    >
      <Formik
        initialValues={initialGuestShipValues}
        onSubmit={handleSubmitForm}
        validationSchema={guestShippingAddressFormSchema}
      >
        {({ isValid, values, dirty }) => (
          <Form>
            <AuthFormField
              title={t(`${TP}.email`, 'Your Email Address')}
              name="email"
              type={AuthFieldType.Email}
              qaAttr={CheckoutQA.GuestEmailBox}
            />
            <EmailSignUp
              name="isNewsletterActive"
              label="newsletter-checkbox"
              text={t(
                `${TP}.newsletterCheckbox`,
                "Sign up to receive Flight Club's email newsletter with special promotions, news and more.",
              )}
              checked={values.isNewsletterActive}
            />
            <AddressFormField
              type={FieldType.FullName}
              title={t(`${TP}.fullName`, '* Full Name')}
              name="name"
              qaAttr={CheckoutQA.NewShippingNameBox}
            />
            <AddressFormField
              type={FieldType.StreetAddress}
              title={t(`${TP}.streetAddress`, '* Street Address')}
              name="address1"
              qaAttr={CheckoutQA.NewShippingAddress1Box}
            />
            <AddressFormField
              type={FieldType.UnitNumber}
              title={t(`${TP}.apartment`, 'Apartment, Suite, Unit, Building, Floor, etc.')}
              name="address2"
              qaAttr={CheckoutQA.NewShippingAddress2Box}
            />
            <AddressFormField
              type={FieldType.City}
              title={t(`${TP}.city`, '* City')}
              name="city"
              qaAttr={CheckoutQA.NewShippingCityBox}
            />
            <AddressFormField
              type={FieldType.ZipCode}
              title={t(`${TP}.zipCode`, {
                defaultValue: '{zipCode}Zip / Postal Code',
                zipCode: isZipRequired ? '* ' : '',
              })}
              name="postalCode"
              qaAttr={CheckoutQA.NewShippingZipCodeBox}
            />
            <CountryDropdownField
              availableCountries={availableCountries}
              title={t(`${TP}.countryCode`, '* Country or Region')}
              name="countryCode"
              qaAttr={CheckoutQA.NewShippingCountryBox}
              onChange={handleCountryChange}
            />
            {isStateRequired && (
              <StateDropdownField
                countryISOCode={countryISOCode}
                title={t(`${TP}.state`, '* State')}
                name="state"
                qaAttr={CheckoutQA.NewShippingStateBox}
              />
            )}
            <AddressFormField
              type={FieldType.Telephone}
              title={t(`${TP}.telephone`, 'Telephone')}
              name="phone"
              qaAttr={CheckoutQA.NewShippingPhoneBox}
            />
            <Pointer>{t(`${TP}.reuiredFields`, '* Required Fields')}</Pointer>
            <ContinueButton
              $fill
              buttonType="primary1"
              type="submit"
              disabled={!(isValid && dirty)}
              qaAttr={CheckoutQA.NewShippingContinueButton}
            >
              {t(`${TP}.continue`, 'Continue')}
            </ContinueButton>
          </Form>
        )}
      </Formik>
    </Spinner>
  )
}

export default GuestShippingAddressForm
